import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Lock, Camera, History } from 'lucide-react';
import { formatDateTimeTR } from '../../utils/timeUtils';
import { mutlakMedyaUrl } from '../../utils/mediaUrl';
import { useAuth } from '../../hooks/useAuth';
import { useAuthContext } from '../../contexts/AuthContext';
import { uploadAvatar } from '../../services/userService';
import { RoleBadge } from '../ui/Badge';
import { sunucuHataMesaji } from '../../utils/apiHata';
import { PROFIL_FOTOGRAFI_MAKS_MB } from '../../shared/constants/businessRules';

interface ProfilePopoverProps {
  isOpen: boolean;
  onClose: () => void;
  onChangePasswordClick: () => void;
  anchorRef: React.RefObject<HTMLDivElement | null>;
}

export const ProfilePopover: React.FC<ProfilePopoverProps> = ({
  isOpen,
  onClose,
  onChangePasswordClick,
  anchorRef,
}) => {
  const { user, userName, userProfilUrl } = useAuth();
  const { updateUser, avatarVersion, bumpAvatarVersion } = useAuthContext();
  const navigate = useNavigate();
  const popoverRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Portal pozisyonu — anchor'ın (avatar butonu) viewport koordinatlarından hesaplanır.
  // Stacking context trap'ini engellemek için popover document.body altına render edilir.
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    if (!isOpen || !anchorRef.current) return;
    const updatePosition = () => {
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8, // mt-2 eşdeğeri
        right: Math.max(8, window.innerWidth - rect.right), // right-0 eşdeğeri, kenardan min 8px
      });
    };
    updatePosition();
    // Pencere resize / scroll sırasında pozisyonu senkronize tut
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, anchorRef]);

  // Cache-bust render-time uygulanır; profil_url DB/localStorage'da temiz kalır.
  const avatarSrc = userProfilUrl
    ? `${mutlakMedyaUrl(userProfilUrl)}${avatarVersion ? `?v=${avatarVersion}` : ''}`
    : '';
  const hasAvatar = !!userProfilUrl;

  // Dış tıklama + Escape ile kapat
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current && !popoverRef.current.contains(target) &&
        anchorRef.current && !anchorRef.current.contains(target)
      ) {
        onClose();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen || !user) return null;

  const getInitials = (name: string) => {
    const names = name.trim().split(' ');
    if (names.length === 0) return '';
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    // Client-side boyut/tip kontrolü (backend Sharp zaten işliyor ama UX için). Sınır
    // sunucuyla aynı sabitten; cümle sayıya göre değişen ek almasın diye "en fazla" der.
    if (file.size > PROFIL_FOTOGRAFI_MAKS_MB * 1024 * 1024) {
      setUploadError(`Dosya en fazla ${PROFIL_FOTOGRAFI_MAKS_MB} MB olabilir`);
      return;
    }
    if (!file.type.startsWith('image/')) {
      setUploadError('Sadece resim dosyası yükleyebilirsiniz');
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      const response = await uploadAvatar(user.id, file);
      // profil_url'i temiz tut; cache-bust session-scoped avatarVersion ile yapılır.
      updateUser({ profil_url: response.data.profil_url });
      bumpAvatarVersion();
    } catch (err) {
      setUploadError(sunucuHataMesaji(err, 'Avatar yüklenemedi'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  if (!position) return null;

  return createPortal(
    <div
      ref={popoverRef}
      className="fixed z-50 w-72 rounded-xl shadow-lg border border-gray-200 bg-white p-4 animate-in fade-in zoom-in-95 duration-150"
      style={{ top: position.top, right: position.right }}
    >
      <div className="flex flex-col items-center">
        <div
          className="relative group cursor-pointer"
          onClick={handleAvatarClick}
          title="Avatar değiştir"
        >
          {hasAvatar ? (
            <img
              src={avatarSrc}
              alt={userName}
              className="w-20 h-20 rounded-full object-cover border border-gray-200"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-primary/10 text-primary flex items-center justify-center text-2xl font-bold border border-primary/20">
              {getInitials(userName || 'K')}
            </div>
          )}

          <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            {uploading ? (
              <div className="text-white text-xs font-medium">Yükleniyor...</div>
            ) : (
              <Camera size={24} className="text-white" />
            )}
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />

        {uploadError && (
          <div className="mt-2 text-xs text-red-600 text-center">{uploadError}</div>
        )}

        <div className="mt-3 text-center">
          <div className="font-semibold text-gray-800 text-sm">{userName}</div>
          <div className="text-xs text-gray-500">{user.email}</div>
          <div className="mt-1.5">
            <RoleBadge role={user.rol} />
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-100 space-y-1.5 text-xs text-gray-500">
        <div className="flex justify-between">
          <span>Kayıt:</span>
          <span className="text-gray-700">{user.kayit_tarihi ? formatDateTimeTR(user.kayit_tarihi) : '—'}</span>
        </div>
        <div className="flex justify-between">
          <span>Son Giriş:</span>
          <span className="text-gray-700">{user.son_giris ? formatDateTimeTR(user.son_giris) : '—'}</span>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-100 space-y-2">
        <button
          type="button"
          onClick={onChangePasswordClick}
          className="w-full flex items-center justify-center gap-2 py-2 px-3 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition"
        >
          <Lock size={16} />
          Şifre Değiştir
        </button>
        <button
          type="button"
          onClick={() => {
            onClose();
            navigate('/me/login-history');
          }}
          className="w-full flex items-center justify-center gap-2 py-2 px-3 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition"
        >
          <History size={16} />
          Giriş Geçmişim
        </button>
      </div>
    </div>,
    document.body
  );
};
